import { ButtonBase, SvgIcon, Collapse, Box, Tooltip } from "@mui/material";
import { LockOutlined, WarningAmberOutlined, ExpandMore as ExpandMoreIcon, ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { RouterLink } from "components/router-link";
import React, { useCallback, useState } from "react";
import type { ReactNode } from "react";
import history from "utils/history";
import { useFeatureState } from "hooks/use-feature";
import strings from "global/constants/StringConstants";
import NavBetaBadge from "./NavBetaBadge";

interface CustomProps {
  active?: boolean;
  children?: ReactNode;
  depth?: number;
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  activeIcon?: ReactNode;
  label?: ReactNode;
  open?: boolean;
  path?: string;
  title: string;
  featureKey?: string;
  beta?: boolean;
}

const MobileNavItem = (props: CustomProps) => {
  const [open, setOpen] = useState<boolean>(!!props.open);
  const featureState = useFeatureState(props.featureKey ?? "");
  const isGated =
    !!props.featureKey &&
    featureState.status !== strings.FEATURE_STATUS_AVAILABLE;
  const gateTooltip = isGated
    ? featureState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
      ? "Upgrade your plan to access this feature"
      : ((featureState as any).disabledMessage ??
        "This feature is temporarily unavailable")
    : "";

  const handleToggle = useCallback((): void => {
    setOpen((prevOpen) => !prevOpen);

    if (props.path) {
      history.push(props.path);
    }
  }, [history, props.path]);

  const depth = props.depth ?? 0;
  const offset = depth * 16;

  // Use actual icon/activeIcon if defined, otherwise fall back to bullet dot.

  let startIcon: ReactNode;
  const actualIcon = props.active ? props.activeIcon : props.icon;

  if (actualIcon) {
    startIcon = actualIcon;
  } else {
    startIcon = (
      <Box
        sx={{
          alignItems: "center",
          display: "center",
          height: 20,
          justifyContent: "center",
          width: 20,
        }}
      >
        <Box
          sx={{
            borderRadius: "50%",
            backgroundColor: "var(--nav-item-icon-color)",
            height: 4,
            width: 4,
            ...(props.active && {
              backgroundColor: "var(--nav-item-icon-active-color)",
              height: 6,
              opacity: 1,
              width: 6,
            }),
          }}
        />
      </Box>
    );
  }

  // Branch

  if (props.children) {
    return (
      <li>
        <ButtonBase
          disabled={props.disabled}
          onClick={handleToggle}
          aria-expanded={open}
          sx={{
            alignItems: "center",
            borderRadius: "11px",
            display: "flex",
            justifyContent: "flex-start",
            pl: `${14 + offset}px`,
            pr: "14px",
            py: "11px",
            textAlign: "left",
            width: "100%",
            ...(props.active && {
              ...(props.depth === 0 && {
                backgroundColor: "var(--nav-item-active-bg)",
              }),
            }),
            "&:hover": {
              backgroundColor:
                props.active && props.depth === 0
                  ? "var(--nav-item-active-bg)"
                  : "var(--nav-item-hover-bg)",
            },
            "&.Mui-focusVisible": {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: "-2px",
            },
          }}
        >
          {startIcon && (
            <Box
              component="span"
              sx={{
                alignItems: "center",
                color: "var(--nav-item-icon-color)",
                display: "inline-flex",
                justifyContent: "center",
                mr: 2,
                ...(props.active && {
                  color: "var(--nav-item-icon-active-color)",
                }),
              }}
            >
              {startIcon}
            </Box>
          )}
          <Tooltip title={gateTooltip} disableHoverListener={!isGated} arrow>
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: isGated
                  ? "var(--nav-item-disabled-color, #C3C3C3)"
                  : "var(--nav-item-color)",
                flexGrow: 1,
                fontFamily: (theme) => theme.typography.fontFamily,
                fontSize: 16,
                fontWeight: 400,
                lineHeight: "24px",
                whiteSpace: "nowrap",
                ...(props.active &&
                  !isGated && {
                    color: "var(--nav-item-active-color)",
                    fontFamily: (theme) => theme.typography.fontFamily,
                    fontWeight: 500,
                    fontSize: 16,
                  }),
                ...(props.disabled && {
                  color: "var(--nav-item-disabled-color)",
                }),
              }}
            >
              {props.title}
              {props.beta && <NavBetaBadge />}
              {isGated &&
                (featureState.status === strings.FEATURE_STATUS_ADMIN_DISABLED ? (
                  <WarningAmberOutlined
                    sx={{ fontSize: 13, opacity: 0.6, flexShrink: 0 }}
                  />
                ) : (
                  <LockOutlined
                    sx={{ fontSize: 13, opacity: 0.6, flexShrink: 0 }}
                  />
                ))}
            </Box>
          </Tooltip>
          <SvgIcon
            sx={{
              color: "var(--nav-item-chevron-color, #9DA4AE)",
              fontSize: 15,
              ml: "auto",
            }}
          >
            {open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </SvgIcon>
        </ButtonBase>
        {open && (
          <Collapse in={open} sx={{ mt: 0.5 }}>
            {props.children}
          </Collapse>
        )}
      </li>
    );
  }

  // Leaf

  const linkProps = props.path
    ? props.external
      ? {
          component: "a",
          href: props.path,
          target: "_blank",
        }
      : {
          component: RouterLink,
          href: props.path,
        }
    : {};

  return (
    <li>
      <ButtonBase
        disabled={props.disabled}
        aria-current={props.active && props.path ? "page" : undefined}
        sx={{
          alignItems: "center",
          borderRadius: "11px",
          display: "flex",
          justifyContent: "flex-start",
          pl: `${14 + offset}px`,
          pr: "14px",
          py: "11px",
          textAlign: "left",
          width: "100%",
          color: "",
          border: "none",
          boxShadow: "none",
          ...(props.active && {
            ...(props.depth === 0 && {
              background: "var(--nav-item-active-bg)",
            }),
          }),
          "&:hover": {
            background:
              props.active && props.depth === 0
                ? "var(--nav-item-active-bg)"
                : "var(--nav-item-hover-bg)",
          },
          "&.Mui-focusVisible": {
            outline: (theme) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: "-2px",
          },
        }}
        {...linkProps}
      >
        {startIcon && (
          <Box
            component="span"
            sx={{
              alignItems: "center",
              color: "var(--nav-item-icon-color)",
              display: "inline-flex",
              justifyContent: "center",
              mr: "12px",
              "& svg": { fontSize: 20 },
              ...(props.active && {
                color: "var(--nav-item-icon-active-color)",
              }),
            }}
          >
            {startIcon}
          </Box>
        )}
        <Tooltip title={gateTooltip} disableHoverListener={!isGated} arrow>
          <Box
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              color: isGated
                ? "var(--nav-item-disabled-color, #C3C3C3)"
                : "var(--nav-item-color)",
              flexGrow: 1,
              fontFamily: (theme) => theme.typography.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              lineHeight: "20px",
              whiteSpace: "nowrap",
              ...(props.active &&
                !isGated && {
                  color: "var(--nav-item-active-color)",
                  fontFamily: (theme) => theme.typography.fontFamily,
                  fontWeight: 700,
                  fontSize: 14,
                }),
              ...(props.disabled && {
                color: "var(--nav-item-disabled-color)",
              }),
            }}
          >
            {props.title}
            {props.beta && <NavBetaBadge />}
            {isGated && (
              <LockOutlined
                sx={{ fontSize: 13, opacity: 0.6, flexShrink: 0 }}
              />
            )}
          </Box>
        </Tooltip>
        {props.label && (
          <Box component="span" sx={{ ml: 2 }}>
            {props.label}
          </Box>
        )}
      </ButtonBase>
    </li>
  );
};

export default MobileNavItem;
